import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Button } from '../components/ui/button.js'
import { Dialog } from '../components/ui/dialog.js'
import { CheckboxField, TextField } from '../components/ui/field.js'
import { Alert, Badge } from '../components/ui/feedback.js'
import { format, t } from '../i18n/index.js'
import { cn } from '../lib/cn.js'
import {
  createTenant,
  listTenants,
  updateTenant,
  type TenantCreated,
  type TenantView,
} from '../lib/tenants.js'

/**
 * The credentials of a freshly created team lead.
 *
 * Shown once and never again: the password is stored as an argon2id hash and
 * there is no mail dispatch in this system, so this panel is the only moment it
 * exists in readable form. It stays open until dismissed deliberately, rather
 * than disappearing on the next render.
 */
function LeadCredentials({ lead, onDone }: { lead: TenantCreated['lead']; onDone: () => void }) {
  const strings = t().tenants

  return (
    <Alert tone="success">
      <p className="font-medium">{strings.passwordHeading}</p>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-text-muted">{strings.leadEmail}</dt>
        <dd>
          <code>{lead.email}</code>
        </dd>
        <dt className="text-text-muted">{strings.leadName}</dt>
        <dd>{lead.name}</dd>
        <dt className="text-text-muted">Passwort</dt>
        <dd>
          <code className="select-all">{lead.password}</code>
        </dd>
      </dl>
      <p className="text-text-muted mt-2 text-xs">{strings.passwordHint}</p>
      <Button size="sm" variant="secondary" className="mt-3" onClick={onDone}>
        {strings.passwordDone}
      </Button>
    </Alert>
  )
}

interface Draft {
  id: string | null
  name: string
  slug: string
  isActive: boolean
  leadName: string
  leadEmail: string
}

function emptyDraft(): Draft {
  return { id: null, name: '', slug: '', isActive: true, leadName: '', leadEmail: '' }
}

function draftFrom(tenant: TenantView): Draft {
  return {
    id: tenant.id,
    name: tenant.name,
    slug: tenant.slug,
    isActive: tenant.isActive,
    leadName: '',
    leadEmail: '',
  }
}

/**
 * Tenant administration, for the platform role alone.
 *
 * A team lead never reaches this page and never learns that other tenants
 * exist. Creating one creates its first team lead in the same step: a tenant
 * nobody can log into is not a tenant.
 */
export function TenantsPage() {
  const strings = t().tenants
  const queryClient = useQueryClient()

  const [showInactive, setShowInactive] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [credentials, setCredentials] = useState<TenantCreated['lead'] | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const query = useQuery({ queryKey: ['tenants'], queryFn: listTenants })

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['tenants'] })
  }

  const save = useMutation({
    mutationFn: async (input: Draft) => {
      if (input.id === null) {
        return createTenant({
          name: input.name,
          slug: input.slug,
          leadName: input.leadName,
          leadEmail: input.leadEmail,
        })
      }

      await updateTenant(input.id, { name: input.name, isActive: input.isActive })
      return null
    },
    onSuccess: async (result) => {
      await refresh()
      setDraft(null)
      setProblem(null)
      setNotice(result === null ? strings.saved : strings.created)
      if (result !== null) setCredentials(result.lead)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const tenants = (query.data ?? []).filter((tenant) => showInactive || tenant.isActive)
  const creating = draft?.id === null

  const saveable =
    draft !== null &&
    draft.name.trim() !== '' &&
    (!creating ||
      (/^[a-z][a-z0-9-]*$/.test(draft.slug) &&
        draft.leadName.trim() !== '' &&
        draft.leadEmail.includes('@')))

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{strings.title}</h1>
          <p className="text-text-muted text-sm">{strings.subtitle}</p>
        </div>
        <Button onClick={() => setDraft(emptyDraft())}>{strings.create}</Button>
      </header>

      {credentials !== null && (
        <LeadCredentials lead={credentials} onDone={() => setCredentials(null)} />
      )}
      {notice !== null && credentials === null && <Alert tone="success">{notice}</Alert>}
      {problem !== null && <Alert tone="danger">{problem}</Alert>}
      {query.isError && <Alert tone="danger">{strings.loadError}</Alert>}

      <div className="flex items-center justify-between">
        <p className="text-text-muted text-sm">
          {query.isPending
            ? t().app.loading
            : tenants.length === 1
              ? strings.countOne
              : format(strings.countMany, { count: tenants.length })}
        </p>
        <CheckboxField
          label={strings.showInactive}
          checked={showInactive}
          onChange={(event) => setShowInactive(event.target.checked)}
        />
      </div>

      {!query.isPending && tenants.length === 0 ? (
        <p className="border-border rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">
          {strings.empty}
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2" data-testid="tenant-list">
          {tenants.map((tenant) => (
            <li
              key={tenant.id}
              className={cn(
                'border-border bg-surface-raised flex flex-col gap-2 rounded-lg border p-4',
                !tenant.isActive && 'opacity-60',
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    {tenant.name}
                    {!tenant.isActive && <Badge tone="warning">{strings.inactive}</Badge>}
                  </p>
                  <p className="text-text-muted text-xs">
                    <code>{tenant.slug}</code>
                  </p>
                </div>
                <Button size="sm" variant="secondary" onClick={() => setDraft(draftFrom(tenant))}>
                  {strings.edit}
                </Button>
              </div>

              <p className="text-text-muted text-sm">
                {format(strings.counts, {
                  users: tenant.userCount,
                  events: tenant.eventCount,
                  items: tenant.itemCount,
                })}
              </p>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={creating ? strings.create : strings.edit}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>
              {strings.cancel}
            </Button>
            <Button
              disabled={!saveable || save.isPending}
              onClick={() => {
                if (draft) save.mutate(draft)
              }}
            >
              {save.isPending ? strings.saving : strings.save}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="flex flex-col gap-4">
            <TextField
              label={strings.name}
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />

            <TextField
              label={strings.slug}
              hint={strings.slugHint}
              value={draft.slug}
              // Frozen after creation: it appears in exports and in support
              // conversations, where a handle that can change is worse than none.
              disabled={!creating}
              onChange={(event) =>
                setDraft({ ...draft, slug: event.target.value.toLowerCase().replace(/\s+/g, '-') })
              }
            />

            {creating ? (
              <fieldset className="border-border flex flex-col gap-4 rounded-lg border p-3">
                <legend className="px-1 text-sm font-medium">{strings.leadHeading}</legend>
                <p className="text-text-muted -mt-2 text-xs">{strings.leadHint}</p>
                <TextField
                  label={strings.leadName}
                  value={draft.leadName}
                  onChange={(event) => setDraft({ ...draft, leadName: event.target.value })}
                />
                <TextField
                  label={strings.leadEmail}
                  type="email"
                  value={draft.leadEmail}
                  onChange={(event) => setDraft({ ...draft, leadEmail: event.target.value })}
                />
              </fieldset>
            ) : (
              <CheckboxField
                label={strings.active}
                hint={strings.activeHint}
                checked={draft.isActive}
                onChange={(event) => setDraft({ ...draft, isActive: event.target.checked })}
              />
            )}
          </div>
        )}
      </Dialog>
    </div>
  )
}
